import React, { useEffect, useRef, useState } from 'react';
import { toYouTubeEmbedSrc } from '../../utils/urls';
import { loadYouTubeApi } from '../../utils/youtubeApi';
import './webinarPlayer.css';

export default function WebinarPlayer({ embedLink, title = 'Diretta webinar' }) {
  const host = useRef(null);
  const playerRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [ended, setEnded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let disposed = false;
    let retriedMuted = false;
    let hasPlayed = false;
    let hasEnded = false;
    const startMuted = window.matchMedia?.('(hover: none) and (pointer: coarse)').matches === true;
    const container = host.current;
    const iframe = document.createElement('iframe');
    const src = new URL(toYouTubeEmbedSrc(embedLink));
    src.searchParams.set('enablejsapi', '1');
    src.searchParams.set('origin', window.location.origin);
    iframe.src = src.toString();
    iframe.title = title;
    iframe.tabIndex = -1;
    iframe.setAttribute('inert', '');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow', 'autoplay; encrypted-media');
    iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-presentation');
    container.appendChild(iframe);
    setMuted(false);
    setPlaying(false);
    setBlocked(false);
    setEnded(false);
    setLoadError(false);

    loadYouTubeApi().then((YT) => {
      if (disposed) return;
      playerRef.current = new YT.Player(iframe, {
        events: {
          onReady: ({ target }) => {
            if (disposed) return;
            if (startMuted) {
              target.mute();
              setMuted(true);
              retriedMuted = true;
            }
            target.playVideo();
          },
          onAutoplayBlocked: ({ target }) => {
            if (disposed || hasEnded) return;
            setBlocked(true);
            if (!retriedMuted) {
              retriedMuted = true;
              target.mute();
              setMuted(true);
              target.playVideo();
            }
          },
          onStateChange: ({ data, target }) => {
            if (disposed || hasEnded) return;
            if (data === 0) {
              hasEnded = true;
              setEnded(true);
              setPlaying(false);
              return;
            }
            setPlaying(data === 1);
            if (data === 1) {
              hasPlayed = true;
              setBlocked(false);
            }
            if (data === 2 && hasPlayed) target.playVideo();
          },
        },
      });
    }).catch(() => {
      if (!disposed) setLoadError(true);
    });

    return () => {
      disposed = true;
      playerRef.current?.destroy();
      playerRef.current = null;
      container.replaceChildren();
    };
  }, [embedLink, title, attempt]);

  const startWithAudio = () => {
    const player = playerRef.current;
    if (!player) return;
    // Keep these calls synchronous with the tap: mobile audio requires a gesture.
    player.unMute();
    player.setVolume(100);
    player.playVideo();
    setMuted(false);
    setBlocked(false);
  };

  return (
    <div className="webinar-player">
      <div ref={host} className="webinar-player__frame" hidden={ended || loadError} />
      {ended && <p className="webinar-player__status" role="status">La diretta è terminata.</p>}
      {loadError && (
        <div className="webinar-player__status" role="status">
          <p>Impossibile caricare la diretta.</p>
          <button type="button" className="btn btn-light" onClick={() => setAttempt(current => current + 1)}>Riprova</button>
        </div>
      )}
      {!ended && !loadError && (muted || blocked) && (
        <button type="button" className="webinar-player__action" onClick={startWithAudio}>
          {playing && muted ? 'Attiva audio' : 'Avvia diretta'}
        </button>
      )}
    </div>
  );
}
